import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useScrambleText } from '../hooks/useScrambleText';

const ScrambleText = ({
  text,
  as = 'h1',
  className = '',
  scrambleSpeed = 40,
  delay = 0,
  duration = 1200,
  perspective = true,
  hoverEffect = false, // Defaulting to false for cleaner typography
  ...props
}) => {
  const { displayText, rescramble } = useScrambleText({
    text,
    scrambleSpeed,
    delay,
    duration,
  });

  const ref = useRef(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!hoverEffect || !ref.current) return;

    // Matikan efek hover pada perangkat seluler/touchscreen
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
    if (isTouchDevice) return;

    const handleMouseMove = (e) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const rotY = ((e.clientX - centerX) / rect.width) * 3;
      const rotX = -((e.clientY - centerY) / rect.height) * 3;

      setRotateX(rotX);
      setRotateY(rotY);
    };

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => {
      setIsHovered(false);
      setRotateX(0);
      setRotateY(0);
    };

    const el = ref.current;
    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hoverEffect]);

  const style = perspective
    ? {
        perspective: '800px',
        transformStyle: 'preserve-3d',
      }
    : {};

  const textStyle = {
    display: 'block',
    transform: hoverEffect ? `rotateX(${rotateX}deg) rotateY(${rotateY}deg)` : 'none',
    transition: isHovered ? 'transform 0.1s ease-out' : 'transform 0.5s ease-out',
    transformStyle: 'preserve-3d',
    willChange: hoverEffect ? 'transform' : 'auto',
  };

  const Tag = motion[as] || motion.h1;

  return (
    <div ref={ref} style={style} className="w-full max-w-full overflow-hidden">
      <Tag
        className={`w-full break-words hyphens-auto select-none ${className}`}
        style={textStyle}
        onClick={rescramble}
        title="Click to rescramble"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        {...props}
      >
        {displayText}
      </Tag>
    </div>
  );
};

export default ScrambleText;